"use client"

import { useEffect, useState, useContext } from "react"
import axios from "axios"
import { motion, AnimatePresence } from "framer-motion"
import { User, Mail, Phone, Save, AlertCircle, CheckCircle, RefreshCw, Lock } from "lucide-react"
import { UserContext } from "../context/UserContext"
import "../styles/perfil.css"

const PerfilUsuario = () => {
  const { token } = useContext(UserContext)
  const [formData, setFormData] = useState({
    nomeCompleto: "",
    email: "",
    cpf: "",
    whatsapp: "",
    senha: "",
    confirmarSenha: "",
  })

  const [mensagem, setMensagem] = useState({ tipo: "", texto: "" })
  const [loading, setLoading] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await axios.get("https://api.ticketdovaqueiro.com/api/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        })
        setFormData((prev) => ({
          ...prev,
          nomeCompleto: response.data.nomeCompleto || "",
          email: response.data.email || "",
          cpf: formatCPF(response.data.cpf || ""),
          whatsapp: formatPhone(response.data.whatsapp || ""),
        }))
      } catch (error) {
        console.error("Erro ao buscar dados:", error)
        setMensagem({
          tipo: "erro",
          texto: "Não foi possível carregar seus dados. Tente novamente mais tarde.",
        })
      } finally {
        setLoading(false)
        setInitialLoad(false)
      }
    }
    fetchData()
  }, [token])

  const formatCPF = (value) => {
    value = value.replace(/\D/g, "").slice(0, 11)
    return value.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, p1, p2, p3, p4) => {
      let result = `${p1}.${p2}.${p3}`
      if (p4) result += `-${p4}`
      return result
    })
  }

  const formatPhone = (value) => {
    value = value.replace(/\D/g, "").slice(0, 11)
    return value.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, p1, p2, p3) => {
      let result = `(${p1}) ${p2}`
      if (p3) result += `-${p3}`
      return result
    })
  }

  const handleChange = (e) => {
    let { name, value } = e.target
    if (name === "cpf") value = formatCPF(value)
    if (name === "whatsapp") value = formatPhone(value)
    setFormData({ ...formData, [name]: value })
  }

  const validateForm = () => {
    // Validar CPF
    const cpfNumerico = formData.cpf.replace(/\D/g, "")
    if (cpfNumerico.length !== 11) {
      setMensagem({ tipo: "erro", texto: "CPF inválido. Verifique e tente novamente." })
      return false
    }

    // Validar WhatsApp
    const whatsappNumerico = formData.whatsapp.replace(/\D/g, "")
    if (whatsappNumerico.length < 10) {
      setMensagem({ tipo: "erro", texto: "Número de WhatsApp inválido. Verifique e tente novamente." })
      return false
    }

    // Validar senha (apenas se estiver preenchida)
    if (formData.senha) {
      if (formData.senha.length < 6) {
        setMensagem({ tipo: "erro", texto: "A senha deve ter pelo menos 6 caracteres." })
        return false
      }

      if (formData.senha !== formData.confirmarSenha) {
        setMensagem({ tipo: "erro", texto: "As senhas não coincidem. Verifique e tente novamente." })
        return false
      }
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMensagem({ tipo: "", texto: "" })

    if (!validateForm()) return

    try {
      setLoading(true)
      const response = await axios.put(
        "https://api.ticketdovaqueiro.com/api/auth/update-profile",
        {
          nomeCompleto: formData.nomeCompleto,
          cpf: formData.cpf.replace(/\D/g, ""),
          whatsapp: formData.whatsapp.replace(/\D/g, ""),
          senha: formData.senha || undefined,
          confirmarSenha: formData.confirmarSenha || undefined,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      setMensagem({ tipo: "sucesso", texto: response.data.mensagem || "Perfil atualizado com sucesso!" })
      setFormData({ ...formData, senha: "", confirmarSenha: "" })
    } catch (error) {
      console.error("Erro ao atualizar:", error)
      setMensagem({
        tipo: "erro",
        texto: error.response?.data?.mensagem || "Erro ao atualizar perfil. Tente novamente.",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="perfil-container"
    >
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <User className="perfil-icon" />
        Meus Dados
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="perfil-subtitle"
      >
        Atualize seus dados e senha de acesso
      </motion.p>

      <AnimatePresence>
        {mensagem.texto && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mensagem ${mensagem.tipo === "sucesso" ? "mensagem-sucesso" : "mensagem-erro"}`}
          >
            {mensagem.tipo === "sucesso" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {mensagem.texto}
          </motion.div>
        )}
      </AnimatePresence>

      {initialLoad ? (
        <div className="loading-container">
          <RefreshCw className="loading-icon" />
          <p>Carregando seus dados...</p>
        </div>
      ) : (
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          onSubmit={handleSubmit}
          className="perfil-form"
        >
          <div className="form-group">
            <label htmlFor="nomeCompleto">
              <User size={16} className="input-icon" />
              Nome Completo:
            </label>
            <input
              type="text"
              id="nomeCompleto"
              name="nomeCompleto"
              value={formData.nomeCompleto}
              onChange={handleChange}
              required
              placeholder="Seu nome completo"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <Mail size={16} className="input-icon" />
              E-mail:
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              disabled
              className="input-disabled"
              placeholder="Seu e-mail"
            />
            <small className="input-help">O e-mail não pode ser alterado.</small>
          </div>

          <div className="form-group">
            <label htmlFor="cpf">
              <User size={16} className="input-icon" />
              CPF:
            </label>
            <input
              type="text"
              id="cpf"
              name="cpf"
              value={formData.cpf}
              onChange={handleChange}
              required
              placeholder="Seu CPF"
            />
          </div>

          <div className="form-group">
            <label htmlFor="whatsapp">
              <Phone size={16} className="input-icon" />
              WhatsApp:
            </label>
            <input
              type="text"
              id="whatsapp"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={handleChange}
              required
              placeholder="Seu número de WhatsApp"
            />
          </div>

          <div className="form-divider">
            <span>Alterar Senha</span>
          </div>

          <div className="form-group">
            <label htmlFor="senha">
              <Lock size={16} className="input-icon" />
              Nova Senha:
            </label>
            <input
              type="password"
              id="senha"
              name="senha"
              value={formData.senha}
              onChange={handleChange}
              placeholder="Deixe em branco para manter a senha atual"
            />
            <small className="input-help">Mínimo de 6 caracteres.</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmarSenha">
              <Lock size={16} className="input-icon" />
              Confirmação:
            </label>
            <input
              type="password"
              id="confirmarSenha"
              name="confirmarSenha"
              value={formData.confirmarSenha}
              onChange={handleChange}
              placeholder="Confirme a nova senha"
            />
          </div>

          <motion.button
            type="submit"
            className="btn-atualizar"
            disabled={loading}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {loading ? (
              <>
                <RefreshCw size={18} className="button-icon spinning" />
                ATUALIZANDO...
              </>
            ) : (
              <>
                <Save size={18} className="button-icon" />
                ATUALIZAR DADOS
              </>
            )}
          </motion.button>
        </motion.form>
      )}
    </motion.div>
  )
}

export default PerfilUsuario
